import { Button } from "@/atoms-react/button/Button.react";
import { Dropdown } from "@/atoms-react/dropdown/index.react";
import Flag from "@/atoms-react/flag/Flag.react";
import InputLabel from "@/atoms-react/input/Input.react";
import { Base } from "@/atoms-react/text/Text.react";
import {
  SchemaRequestCreateTravelRule,
  TravelRulePersonType,
  TravelRuleWalletType,
  WalletDepositProps
} from "@/features/kypay/types";
import { usei18n } from "@/features/utils/hooks/use-i18n";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

export const TravelRulesForm = ({
  onValidate,
  isPendingCreation,
  countries,
  walletRule,
  isPerson,
  disable
}: WalletDepositProps) => {
  const { t } = useTranslation(["pass", "wallet"]);
  const { getLanguage } = usei18n();
  const {
    handleSubmit,
    control,
    formState: { errors, isValid },
    setValue,
    reset,
    watch
  } = useForm<SchemaRequestCreateTravelRule>({
    defaultValues: {
      companyInformation: !isPerson
        ? {
            address: walletRule?.companyAddress ?? null,
            countryCodeAlpha3: walletRule?.companyCountryAlpha3 ?? null,
            name: walletRule?.companyName ?? null,
            rcs: walletRule?.companyRcs ?? null
          }
        : null,
      isSamePerson: isPerson,
      name: walletRule?.walletName ?? null,
      type: walletRule?.walletType ?? null,
      typeName: walletRule?.walletTypeName ?? null,
      naturalInformation: !isPerson
        ? {
            countryCodeAlpha3: walletRule?.userCountryAlpha3 ?? null,
            firstName: walletRule?.userFirstName ?? null,
            lastName: walletRule?.userLastName ?? null
          }
        : null
    }
  });

  const onSubmit: SubmitHandler<SchemaRequestCreateTravelRule> = (data) => {
    const payload = {
      ...data,
      companyInformation: !data.companyInformation
        ? undefined
        : {
            ...data.companyInformation,
            rcs: !data.companyInformation.rcs ? undefined : data.companyInformation.rcs
          },
      isSamePerson: isPerson,
      naturalInformation: !data.naturalInformation ? undefined : data.naturalInformation
    };
    onValidate(payload);
  };
  const labelInputDivClassName = "md:flex  items-center ";
  const inputClassName = "flex grow overflow-auto";
  const [walletHolder, setWalletHolder] = useState<boolean | null>(null);
  useEffect(() => {
    if (!walletRule) {
      if (walletHolder) setWalletHolder(null);
      reset({
        companyInformation: null,
        name: null,
        naturalInformation: null,
        type: null,
        typeName: null
      });
    } else {
      if (isPerson) setWalletHolder(null);
      else {
        setWalletHolder(!!walletRule.userFirstName);
      }
      reset({
        companyInformation: !isPerson
          ? {
              address: walletRule?.companyAddress ?? null,
              countryCodeAlpha3: walletRule?.companyCountryAlpha3 ?? null,
              name: walletRule?.companyName ?? null,
              rcs: walletRule?.companyRcs ?? null
            }
          : null,
        isSamePerson: isPerson,
        name: walletRule?.walletName ?? null,
        type: walletRule?.walletType ?? null,
        typeName: walletRule?.walletTypeName ?? null,
        naturalInformation: !isPerson
          ? {
              countryCodeAlpha3: walletRule?.userCountryAlpha3 ?? null,
              firstName: walletRule?.userFirstName ?? null,
              lastName: walletRule?.userLastName ?? null
            }
          : null
      });
    }
  }, [walletRule, reset]);
  const walletHolderType: {
    typeFr: string;
    typeEn: string;
    type: TravelRulePersonType;
  }[] = [
    {
      typeFr: "Une personne Physique",
      typeEn: "A natural person",
      type: TravelRulePersonType.NATURAL
    },
    {
      typeFr: "Une personne morale",
      typeEn: "A legal person",
      type: TravelRulePersonType.LEGAL
    }
  ];
  const walletType: {
    defaultName: string;
    type: TravelRuleWalletType;
  }[] = [
    {
      defaultName: "Exchange (Binance, Coinbase, ...)",
      type: TravelRuleWalletType.THIRD_PARTY
    },
    {
      defaultName: "Web3 (Metamask, Ledger, ...)",
      type: TravelRuleWalletType.SELF
    }
  ];

  return (
    <div>
      <div className="flex w-full flex-col">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className={twMerge(labelInputDivClassName, "mt-2")}>
            <div className={inputClassName}>
              <Controller
                control={control}
                rules={{
                  required: true
                }}
                render={({ field: { onChange, value } }) => (
                  <InputLabel
                    placeholder={""}
                    disabled={disable}
                    label={t(
                      "wallet:kypay.modal_deposit_funds.steps.step_2.crypto.travel_rule.wallet_name"
                    )}
                    error={errors.name ? t("atoms:general.required") : ""}
                    value={value as string}
                    onChange={(value) => {
                      onChange(value);
                    }}
                  />
                )}
                name="name"
              />
            </div>
          </div>
          {!isPerson && (
            <div className={twMerge(labelInputDivClassName, "mt-2")}>
              <div className={inputClassName}>
                <Dropdown
                  search={true}
                  label={t(
                    "wallet:kypay.modal_deposit_funds.steps.step_2.crypto.travel_rule.wallet_owner"
                  )}
                  disabled={disable}
                  items={
                    (walletHolderType ?? []).map((data) => ({
                      key: data.type,
                      value: getLanguage() === "fr" ? data.typeFr : data.typeEn,
                      render: ({ key }) => (
                        <div className={"flex w-full pr-5"} key={key}>
                          <Base>{getLanguage() === "fr" ? data.typeFr : data.typeEn}</Base>
                        </div>
                      )
                    })) ?? []
                  }
                  value={
                    walletHolder === null
                      ? ""
                      : walletHolder === true
                      ? TravelRulePersonType.NATURAL
                      : TravelRulePersonType.LEGAL
                  }
                  className={twMerge("mt-2 w-full w-full")}
                  onChange={({ key }) => {
                    if (key === TravelRulePersonType.NATURAL) {
                      if (watch("companyInformation")) setValue("companyInformation", null);
                      setWalletHolder(true);
                    } else if (key === TravelRulePersonType.LEGAL) {
                      if (watch("naturalInformation")) setValue("naturalInformation", null);
                      setWalletHolder(false);
                    }
                  }}
                />
              </div>
            </div>
          )}{" "}
          {walletHolder || isPerson ? (
            <>
              {!isPerson && (
                <div className={twMerge("mt-4 flex w-full flex-col flex-nowrap md:flex-row")}>
                  <div className={"w-full"}>
                    <Controller
                      control={control}
                      rules={{
                        required: true
                      }}
                      render={({ field: { onChange, value } }) => (
                        <InputLabel
                          placeholder={""}
                          disabled={disable}
                          className=""
                          required={false}
                          label={t(
                            "wallet:kypay.modal_deposit_funds.steps.step_2.crypto.travel_rule.wallet_owner_lastName"
                          )}
                          error={
                            errors.naturalInformation?.lastName ? t("atoms:general.required") : ""
                          }
                          value={value as string}
                          onChange={(value) => {
                            onChange(value);
                          }}
                        />
                      )}
                      name="naturalInformation.lastName"
                    />
                  </div>
                  <div className={"w-full md:ml-2"}>
                    <Controller
                      control={control}
                      rules={{
                        required: true
                      }}
                      render={({ field: { onChange, value } }) => (
                        <InputLabel
                          disabled={disable}
                          placeholder={""}
                          label={t(
                            "wallet:kypay.modal_deposit_funds.steps.step_2.crypto.travel_rule.wallet_owner_firstName"
                          )}
                          error={
                            errors.naturalInformation?.firstName ? t("atoms:general.required") : ""
                          }
                          value={value as string}
                          onChange={(value) => {
                            onChange(value);
                          }}
                        />
                      )}
                      name="naturalInformation.firstName"
                    />
                  </div>
                </div>
              )}

              {!isPerson && (
                <div className={twMerge("mt-4 w-full")}>
                  <Controller
                    control={control}
                    rules={{
                      required: true
                    }}
                    render={({ field: { onChange, value } }) => (
                      <Dropdown
                        initialValue={value}
                        onChange={({ key }) => {
                          onChange(key);
                        }}
                        label={t(
                          "wallet:kypay.modal_deposit_funds.steps.step_2.crypto.travel_rule.wallet_owner_country"
                        )}
                        disabled={disable}
                        search={true}
                        className={twMerge(
                          "w-62 w-full",
                          errors.naturalInformation?.countryCodeAlpha3 && "border-red-500"
                        )}
                        items={
                          countries?.map((country) => ({
                            key: country.alpha3,
                            value: country.defaultName,
                            icon: <Flag countryCode={country.alpha2.toLowerCase()} />,
                            render: ({ key }: { key: string }) => (
                              <div className={"flex w-full"} key={key}>
                                <Flag
                                  countryCode={country.alpha2}
                                  className="!w-6 !h-6 rounded-sm"
                                />
                                <Base className={"max-8 truncate md:ml-2"}>
                                  {country.defaultName}
                                </Base>
                              </div>
                            )
                          })) ?? []
                        }
                      />
                    )}
                    name="naturalInformation.countryCodeAlpha3"
                  />
                </div>
              )}
            </>
          ) : walletHolder === false ? (
            <>
              <div className={twMerge("mt-4 flex w-full flex-col flex-nowrap md:flex-row")}>
                <div className={"w-full"}>
                  <Controller
                    control={control}
                    rules={{
                      required: true
                    }}
                    render={({ field: { onChange, value } }) => (
                      <InputLabel
                        placeholder={""}
                        label={t(
                          "wallet:kypay.modal_deposit_funds.steps.step_2.crypto.travel_rule.company_owner"
                        )}
                        error={errors.companyInformation?.name ? t("atoms:general.required") : ""}
                        disabled={disable}
                        value={value as string}
                        onChange={(value) => {
                          onChange(value);
                        }}
                      />
                    )}
                    name="companyInformation.name"
                  />
                </div>
                <div className={"w-full md:ml-2"}>
                  <Controller
                    control={control}
                    rules={{
                      required: true
                    }}
                    render={({ field: { onChange, value } }) => (
                      <InputLabel
                        placeholder={""}
                        label={t(
                          "wallet:kypay.modal_deposit_funds.steps.step_2.crypto.travel_rule.company_owner_address"
                        )}
                        error={
                          errors.companyInformation?.address ? t("atoms:general.required") : ""
                        }
                        disabled={disable}
                        value={value as string}
                        onChange={(value) => {
                          onChange(value);
                        }}
                      />
                    )}
                    name="companyInformation.address"
                  />
                </div>
              </div>

              <div className={twMerge("mt-4 flex w-full flex-col flex-nowrap md:flex-row")}>
                <div className={"w-full"}>
                  <Controller
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <InputLabel
                        label={t(
                          "wallet:kypay.modal_deposit_funds.steps.step_2.crypto.travel_rule.company_owner_rcs"
                        )}
                        disabled={disable}
                        required={false}
                        value={value as string}
                        onChange={(value) => {
                          onChange(value);
                        }}
                      />
                    )}
                    name="companyInformation.rcs"
                  />
                </div>
                <div className={"w-full md:ml-2"}>
                  <Controller
                    control={control}
                    rules={{
                      required: true
                    }}
                    render={({ field: { onChange, value } }) => (
                      <Dropdown
                        initialValue={value}
                        onChange={({ key }) => {
                          onChange(key);
                        }}
                        label={t(
                          "wallet:kypay.modal_deposit_funds.steps.step_2.crypto.travel_rule.company_owner_country"
                        )}
                        className={twMerge(
                          "w-62 w-full",
                          errors.companyInformation?.countryCodeAlpha3 && "border-red-500"
                        )}
                        search={true}
                        disabled={disable}
                        items={
                          countries?.map((country) => ({
                            key: country.alpha3,
                            value: country.defaultName,
                            icon: <Flag countryCode={country.alpha2.toLowerCase()} />,
                            render: ({ key }: { key: string }) => (
                              <div className={"flex w-full"} key={key}>
                                <Flag
                                  countryCode={country.alpha2}
                                  className="!w-6 !h-6 rounded-sm"
                                />
                                <Base className={"max-8 truncate md:ml-2"}>
                                  {country.defaultName}
                                </Base>
                              </div>
                            )
                          })) ?? []
                        }
                      />
                    )}
                    name="companyInformation.countryCodeAlpha3"
                  />
                </div>
              </div>
            </>
          ) : (
            <></>
          )}
          <div className={twMerge("mt-4 flex w-full flex-col flex-nowrap md:flex-row")}>
            <div className={"w-full"}>
              <Controller
                control={control}
                rules={{
                  required: true
                }}
                render={({ field: { onChange, value } }) => (
                  <Dropdown
                    search={true}
                    label={t(
                      "wallet:kypay.modal_deposit_funds.steps.step_2.crypto.travel_rule.wallet_type"
                    )}
                    items={
                      (walletType ?? []).map((data) => ({
                        key: data.type,
                        value: data.defaultName,
                        render: ({ key }) => (
                          <div className={"flex w-full pr-5"} key={key}>
                            <Base>{data.defaultName}</Base>
                          </div>
                        )
                      })) ?? []
                    }
                    disabled={disable}
                    value={value ? TravelRuleWalletType[`${value}`] : ""}
                    className={twMerge("w-62 w-full", errors.type && "border-red-500")}
                    onChange={({ key }) => {
                      onChange(key.toString());
                    }}
                  />
                )}
                name="type"
              />
            </div>
            <div className={"w-full md:ml-2"}>
              <Controller
                control={control}
                rules={{
                  required: true
                }}
                render={({ field: { onChange, value } }) => (
                  <InputLabel
                    placeholder={""}
                    label={t(
                      "wallet:kypay.modal_deposit_funds.steps.step_2.crypto.travel_rule.service_name"
                    )}
                    error={errors.typeName ? t("atoms:general.required") : ""}
                    value={value as string}
                    disabled={disable}
                    onChange={(value) => {
                      onChange(value);
                    }}
                  />
                )}
                name="typeName"
              />
            </div>
          </div>
          {!disable && (
            <div className="mt-8 flex w-full flex-row justify-center">
              <Button
                size="md"
                loading={isPendingCreation}
                disabled={!isValid}
                type="submit"
                className="w-auto rounded-lg bg-primary"
              >
                {" "}
                {t("wallet:kypay.modal_deposit_funds.steps.step_2.crypto.travel_rule.submit")}{" "}
              </Button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};
